import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import DashboardLayout from "../../layouts/DashboardLayout";
import LGModal from "../../molecules/LGModal";
import { LGTabPanel } from "../../molecules/LGTabPanel";
import ExportForm from "../../organisms/Exports/ExportForm";
import CrudExportItems from "../../organisms/Exports/CrudExportItems";
import EmpaquePanel from "../../organisms/Exports/Empaque/EmpaquePanel";
import ExportDocumentsModal from "../../organisms/Exports/ExportDocumentsModal";
import { getExportById } from "../../api/exports";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

// Granular detail page for an Export — mirrors PODetailMaterialScreen.js for
// Purchase Orders: reached after the header-only ExportForm modal is submitted
// (create or edit), or directly via the Exports list's "Ver contenido" shortcut.
const ExportDetailScreen = () => {
  const location = useLocation();
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();
  const id = location.state?.id;
  const companyId = selectedCompany?.id;

  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headerModalOpen, setHeaderModalOpen] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const fetchExport = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await getExportById(id);
      setExportData(res.data);
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoading(false);
    }
  }, [id, openSnack]);

  useEffect(() => {
    fetchExport();
  }, [fetchExport]);

  const handleHeaderModalClose = () => {
    setHeaderModalOpen(false);
    fetchExport();
  };

  if (!id) {
    return (
      <DashboardLayout>
        <Box p={4}>
          <Typography>No se encontró la exportación solicitada.</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (!selectedCompany) {
    return (
      <DashboardLayout>
        <Box p={4}>
          <Typography>Seleccione una compañía primero</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box p={2}>
        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                position: "sticky",
                top: 50,
                backgroundColor: "background.default",
                zIndex: 1000,
                paddingTop: 2,
                paddingBottom: 2,
              }}
            >
              <Typography variant="h4">Exportación {exportData?.code}</Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Cliente</Typography>
                  <Typography variant="body1">{exportData?.consignee?.name || "—"}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Forwarder</Typography>
                  <Typography variant="body1">{exportData?.forwarder?.name || "—"}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Factura</Typography>
                  <Typography variant="body1">
                    {exportData?.invoiceNo || "—"}
                    {exportData?.invoiceDate ? ` (${exportData.invoiceDate})` : ""}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Estatus</Typography>
                  <Typography variant="body1">{exportData?.status?.name || "—"}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ marginTop: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setHeaderModalOpen(true)}
                  startIcon={<Icon>edit</Icon>}
                >
                  Modificar Exportación
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setDocumentsModalOpen(true)}
                  startIcon={<Icon>description</Icon>}
                >
                  Descargar documentos
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <LGModal
                open={headerModalOpen}
                handleClose={handleHeaderModalClose}
                header="Editar exportación"
              >
                <ExportForm id={id} companyId={companyId} />
              </LGModal>
              <LGModal
                open={documentsModalOpen}
                handleClose={() => setDocumentsModalOpen(false)}
                header="Descargar documentos"
              >
                <ExportDocumentsModal props={{ id }} />
              </LGModal>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                >
                  <Tab label="Items" />
                  <Tab label="Empaque" />
                </Tabs>
              </Box>

              <LGTabPanel value={activeTab} index={0} sx={{ "& > .MuiBox-root": { py: 2, px: 0 } }}>
                <Typography variant="h5" mb={1}>Items de exportación</Typography>
                <CrudExportItems
                  exportId={id}
                  companyId={companyId}
                  onItemsChanged={fetchExport}
                  isAgsExport={Boolean(exportData?.agsExport)}
                />
              </LGTabPanel>

              <LGTabPanel value={activeTab} index={1} sx={{ "& > .MuiBox-root": { py: 2, px: 0 } }}>
                <EmpaquePanel exportData={exportData} companyId={companyId} />
              </LGTabPanel>
            </Grid>
          </Grid>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default React.memo(ExportDetailScreen);
